import type { PricingCompanySize } from '@recruitee/pricing-types';
import type { BillingPeriod, BillingPlanTier } from '@recruitee/products-types';

export type PricingFeatureSubscription = SubscriptionBase;

export type Subscription = SubscriptionBase & {
  addons: Array<SlotsAddon | TextingAddon | KiwiAddon | SsoAddon>;
  minimalCompanySize: PricingCompanySize | null;
};

export type SubscriptionBase = {
  active: boolean;
  amount: number;
  autoCollection: boolean;
  billingPeriod: BillingPeriod;
  blockAt: string;
  contractTerm: ContractTerm | null;
  currency: 'EUR' | 'USD' | 'GBP';
  currentPeriodEnd?: string;
  currentPeriodStart?: string;
  dueInvoicesAmount: number;
  dueInvoicesCount: number;
  id: number;
  immutable?: boolean;
  manual: boolean;
  pastDue: boolean;
  planQuantity: number | null;
  scheduledChange: ScheduledChange | null;
  selectedCompanySize: PricingCompanySize | null;
  showMigrationBanner?: boolean;
  slots: number;
  status: SubscriptionStatus;
  tier: BillingPlanTier;
  unlimited: boolean;
  validFrom: string;
  validUntil: string | null;
};

export type SubscriptionPaymentType = 1 | 12;

export type ContractEndAction = 'renew' | 'renew_once' | 'cancel' | 'evergreen';

export type ContractTerm = {
  actionAtTermEnd: ContractEndAction;
  contractStart: string;
  contractEnd: string;
  status: string;
  cancellationCutoffPeriod: number;
};

export type SubscriptionStatus =
  | 'closed' // TODO: do we still have this one ?
  | 'incomplete'
  | 'incomplete_expired'
  | 'active'
  | 'expired'
  | 'blocked'
  | 'canceled'
  | 'non_renewing'
  | 'past_due'
  | 'inactive'
  | 'trialing'
  | 'paused'
  | 'future';

export enum AddonType {
  Slots = 'slots',
  Texting = 'texting',
  Kiwi = 'kiwihr_plus',
  Sso = 'sso',
  Onboarding = 'onboarding',
}

export type SlotsAddon = {
  type: AddonType.Slots;
  quantity: number;
};

export type SsoAddon = {
  type: AddonType.Sso;
  quantity: number;
};

export type TextingAddon = {
  type: AddonType.Texting;
  quantity: number;
  totalCredits: number;
  creditsUsed: number;
  validUntil: string;
};

export type KiwiAddon = {
  type: AddonType.Kiwi;
  quantity: number;
  amount: number;
  unitPrice: number;
};

export type Addon = { type: AddonType; quantity: number };

export interface CancelPayload {
  reason: string;
  value: string | null;
  otherSolution: string;
  likelyToReturn: string;
  whatYouLiked: string;
  reasonToStay: string;
}

type ScheduledChangeSummary = {
  plan?: {
    tier?: BillingPlanTier;
    billingPeriod?: number;
  };
  addons?: Array<{
    quantity: number | null;
    type: AddonType;
  }>;
};

type ChangeActionTier = {
  change: {
    from: {
      tier: BillingPlanTier;
    };
    to: {
      tier: BillingPlanTier;
    };
  };
  object: 'plan';
  type: 'change/tier';
};

type ChangeActionBillingPeriod = {
  change: {
    from: {
      billingPeriod: number;
    };
    to: {
      billingPeriod: number;
    };
  };
  object: 'plan';
  type: 'change/billing_period';
};

type ChangeActionAddon = {
  change: {
    from: {
      quantity: number | null;
      type: AddonType;
    };
    to: {
      quantity: number | null;
      type: AddonType;
    };
  };
  object: 'addon';
  type: 'change/quantity';
};

type ScheduledChangeAction = ChangeActionTier | ChangeActionBillingPeriod | ChangeActionAddon;

export type ScheduledChange = {
  actions: ScheduledChangeAction[];
  summarized: ScheduledChangeSummary;
};
